import React, { useRef, useEffect, useState } from 'react';
import useIsMobile from './useIsMobile';
import useChatStore from '../../stores/useChatStore';
import FlerpieAvatar from './FlerpieAvatar';
import useSSEStream from './useSSEStream';

const FlerpieInputBar = () => {
  const isStreaming = useChatStore((s) => s.isStreaming);
  const activeConversationId = useChatStore((s) => s.activeConversationId);
  const cooldownUntil = useChatStore((s) => s.cooldownUntil);
  const { sendMessage } = useSSEStream();
  const [input, setInput] = useState('');
  const [cooldownPct, setCooldownPct] = useState(0);
  const [cooldownSecs, setCooldownSecs] = useState(0);
  const textareaRef = useRef(null);
  const isMobile = useIsMobile();

  // Cooldown countdown tick
  useEffect(() => {
    if (!cooldownUntil) { setCooldownPct(0); setCooldownSecs(0); return; }
    const duration = 60000; // matches the 60s cooldown
    const tick = () => {
      const remaining = cooldownUntil - Date.now();
      if (remaining <= 0) {
        useChatStore.getState().clearCooldown();
        setCooldownPct(0);
        setCooldownSecs(0);
        return;
      }
      setCooldownPct(remaining / duration);
      setCooldownSecs(Math.ceil(remaining / 1000));
    };
    tick();
    const id = setInterval(tick, 100);
    return () => clearInterval(id);
  }, [cooldownUntil]);

  const isCoolingDown = cooldownPct > 0;

  // A reply started elsewhere (a note's Reply) lands here once, caret at the end.
  const draft = useChatStore((s) => s.draft);
  useEffect(() => {
    if (!draft) return;
    setInput(draft);
    useChatStore.getState().setDraft('');
    requestAnimationFrame(() => {
      const ta = textareaRef.current;
      if (!ta) return;
      ta.focus();
      ta.setSelectionRange(draft.length, draft.length);
    });
  }, [draft]);

  // Auto-resize textarea
  useEffect(() => {
    const ta = textareaRef.current;
    if (!ta) return;
    ta.style.height = 'auto';
    ta.style.height = Math.min(ta.scrollHeight, 120) + 'px';
  }, [input]);

  // Focus on mount (desktop only — on mobile, auto-focus forces the keyboard
  // open/closed unpredictably during streaming transitions)
  useEffect(() => {
    if (textareaRef.current && !isStreaming && !isMobile) {
      textareaRef.current.focus();
    }
  }, [isStreaming]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSend = () => {
    const trimmed = input.trim();
    if (!trimmed || isStreaming || isCoolingDown) return;
    setInput('');
    sendMessage(activeConversationId, trimmed);
  };

  // Flerpie's one suggested next question sits in the box as gray text; Tab takes it.
  const suggestion = useChatStore((s) => {
    for (let i = s.messages.length - 1; i >= 0; i--) {
      const m = s.messages[i];
      if (m.role === 'assistant') return m.suggestion || null;
    }
    return null;
  });

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    } else if (e.key === 'Tab' && !e.shiftKey && !input && suggestion) {
      e.preventDefault();
      setInput(suggestion);
    }
  };

  const currentEmotion = useChatStore((s) => s.currentEmotion);

  return (
    <div
      className="flex-shrink-0 px-3 pb-3 pt-2"
      style={{ borderTop: '1px solid rgba(255,204,51,0.04)' }}
    >
      {/* Streaming indicator */}
      {isStreaming && (
        <div className="flex items-center justify-center mb-2 px-1">
          <span className="text-[11px] text-gray-500">Flerpie is thinking...</span>
        </div>
      )}

      {/* Avatar + Input area */}
      <div className="flex items-end gap-2">
        {/* Flerpie avatar — overflows into chat area, click to close */}
        <button
          onClick={() => useChatStore.getState().toggleOpen()}
          className="relative z-30 flex-shrink-0 self-end -mb-2 -ml-1 -mt-8 cursor-pointer
                     transition-transform duration-200 hover:scale-110 active:scale-95"
          title="Close Flerpie"
          aria-label="Close Flerpie"
        >
          <FlerpieAvatar emotion={currentEmotion} size={72} />
        </button>

        {/* Input box */}
        <div
          className="flex-1 flex items-end gap-2 rounded-xl px-3 py-2 transition-all duration-200 relative overflow-hidden"
          style={{
            background: 'rgba(58,58,58,0.4)',
            border: isCoolingDown ? '1px solid rgba(255,204,51,0.15)' : '1px solid rgba(255,204,51,0.06)',
          }}
          onClick={() => !isCoolingDown && textareaRef.current?.focus()}
        >
          {/* Cooldown drain bar */}
          {isCoolingDown && (
            <div
              className="absolute inset-0 pointer-events-none"
              style={{
                background: 'linear-gradient(90deg, rgba(255,204,51,0.08) 0%, rgba(255,170,0,0.12) 100%)',
                transformOrigin: 'left',
                transform: `scaleX(${cooldownPct})`,
                borderRadius: 'inherit',
              }}
            />
          )}
          <textarea
            ref={textareaRef}
            value={input}
            onChange={(e) => { if (!isStreaming && !isCoolingDown) setInput(e.target.value); }}
            onKeyDown={handleKeyDown}
            placeholder={isCoolingDown ? `Cooling down... ${cooldownSecs}s` : isStreaming ? 'Wait for Flerpie to finish...' : suggestion ? `Try: ${suggestion}` : 'Ask Flerpie something...'}
            disabled={(!isMobile && isStreaming) || isCoolingDown}
            readOnly={isMobile && isStreaming}
            maxLength={10000}
            rows={1}
            className={`flex-1 bg-transparent text-[13px] text-gray-200 placeholder:text-on-surface-variant
                       resize-none outline-none leading-relaxed relative z-10
                       ${isStreaming || isCoolingDown ? 'opacity-40' : ''}`}
            style={{ maxHeight: 120 }}
          />
          <button
            aria-label="Send message"
            onClick={handleSend}
            disabled={!input.trim() || isStreaming || isCoolingDown}
            className="flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-lg
                       transition-all duration-200 disabled:opacity-20 relative z-10"
            style={{
              background: input.trim() && !isStreaming && !isCoolingDown ? 'rgba(255,204,51,0.15)' : 'transparent',
              color: input.trim() && !isStreaming && !isCoolingDown ? '#ffcc33' : '#555',
            }}
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path
                d="M2 8L14 8M14 8L9 3M14 8L9 13"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        </div>
      </div>
      {/* Character counter — visible when approaching limit */}
      {input.length > 9000 && (
        <div className="text-right pr-1 pt-1">
          <span className={`text-[10px] ${input.length > 9800 ? 'text-red-400' : 'text-gray-600'}`}>
            {input.length.toLocaleString()}/10,000
          </span>
        </div>
      )}
    </div>
  );
};

export default FlerpieInputBar;
